import React from 'react';
import CanvasRenderer from '../CanvasRenderer';

// ─── FREE DIV ────────────────────────────────────────────────────────────────
export function FreeDiv({ comp, onChildSelect, selectedChildId }) {
  const children = comp.children || [];
  const cols = comp.childCols || 1;

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      background: comp.bg || '#fff',
      border: `${comp.borderWidth || 1}px ${comp.borderStyle || 'solid'} ${comp.borderColor || '#E2E5EA'}`,
      borderRadius: comp.borderRadius || 8,
      padding: `${comp.padT ?? 16}px ${comp.padR ?? 16}px ${comp.padB ?? 16}px ${comp.padL ?? 16}px`,
      display: cols > 1 ? 'grid' : 'flex',
      flexDirection: cols === 1 ? 'column' : undefined,
      gridTemplateColumns: cols > 1 ? `repeat(${cols}, 1fr)` : undefined,
      gap: comp.childGap || 12,
      alignItems: 'start',
    }}>
      {children.length === 0 ? (
        <span style={{ fontSize: 11, color: '#9CA3AF', margin: 'auto' }}>
          ▢ Free Div — add children in panel →
        </span>
      ) : (
        children.map((child, i) => (
          <div key={child.id}
            onClick={(e) => { e.stopPropagation(); onChildSelect && onChildSelect(child.id); }}
            style={{ outline: selectedChildId === child.id ? '2px solid #2563EB' : 'none', outlineOffset: 2, borderRadius: 4, cursor: 'pointer', position: 'relative' }}
          >
            <CanvasRenderer comp={child} />
            {selectedChildId === child.id && (
              <div style={{ position: 'absolute', top: -16, left: 0, fontSize: 9, fontWeight: 600, background: '#2563EB', color: '#fff', padding: '1px 6px', borderRadius: '3px 3px 0 0', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                #{i + 1} {child.type}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ─── COLUMNS ─────────────────────────────────────────────────────────────────
export function Columns({ comp, onChildSelect, selectedChildId }) {
  const colDefs = comp.columnDefs || Array.from({ length: comp.numCols || 2 }, (_, i) => ({
    id: `col_${i}`, bg: 'transparent', borderColor: '#CDD0D8', borderRadius: 6, padding: 8, children: [],
  }));

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      display: 'grid',
      gridTemplateColumns: `repeat(${colDefs.length}, 1fr)`,
      gap: comp.colGap || 20,
    }}>
      {colDefs.map((col, ci) => (
        <div key={col.id || ci} style={{
          background: col.bg || 'transparent',
          border: `1px dashed ${col.borderColor || '#CDD0D8'}`,
          borderRadius: col.borderRadius || 6,
          padding: col.padding || 8,
          minHeight: 60,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {!col.children?.length ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 12 }}>
              Col {ci + 1}
            </div>
          ) : (
            col.children.map((child, i) => (
              <div key={child.id}
                onClick={(e) => { e.stopPropagation(); onChildSelect && onChildSelect(child.id); }}
                style={{ outline: selectedChildId === child.id ? '2px solid #2563EB' : 'none', outlineOffset: 2, borderRadius: 4, cursor: 'pointer', position: 'relative' }}
              >
                <CanvasRenderer comp={child} />
              </div>
            ))
          )}
        </div>
      ))}
    </div>
  );
}

// ─── SECTION CARD ────────────────────────────────────────────────────────────
export function SectionWithChildren({ comp, onChildSelect, selectedChildId }) {
  const children = comp.children || [];
  const cols = comp.childCols || 2;
  const gap = comp.childGap || 16;

  const shadow =
    comp.shadow === 'sm' ? '0 1px 4px rgba(0,0,0,.06)' :
    comp.shadow === 'md' ? '0 4px 16px rgba(0,0,0,.1)' : 'none';

  return (
    <div style={{
      width: '100%', boxSizing: 'border-box',
      background: comp.bg || '#fff',
      borderRadius: comp.borderRadius || 10,
      border: comp.borderEnabled ? `${comp.borderWidth || 1}px ${comp.borderStyle || 'solid'} ${comp.borderColor || '#E2E5EA'}` : 'none',
      boxShadow: shadow,
      padding: `${comp.padT ?? 20}px ${comp.padR ?? 20}px ${comp.padB ?? 20}px ${comp.padL ?? 20}px`,
    }}>
      {comp.showTitle && (
        <div style={{
          fontSize: comp.titleFontSize || 15, fontWeight: comp.titleFontWeight || 600,
          color: comp.titleColor || '#111827', marginBottom: gap,
          paddingBottom: comp.showDivider ? 12 : 0,
          borderBottom: comp.showDivider ? `1px solid ${comp.dividerColor || '#E2E5EA'}` : 'none',
        }}>
          {comp.title}
        </div>
      )}

      {children.length === 0 ? (
        <div style={{
          minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'repeating-linear-gradient(45deg,#F7F8FA,#F7F8FA 4px,transparent 4px,transparent 16px)',
          borderRadius: 6, color: '#9CA3AF', fontSize: 12,
          border: '1px dashed #CDD0D8',
        }}>
          Section is empty — add children in the panel →
        </div>
      ) : (
        <div style={{
          display: comp.childLayout === 'grid' ? 'grid' : comp.childLayout === 'flex' ? 'flex' : 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          flexWrap: 'wrap',
          gap,
        }}>
          {children.map((child, i) => (
            <div key={child.id}
              onClick={(e) => { e.stopPropagation(); onChildSelect && onChildSelect(child.id); }}
              style={{
                outline: selectedChildId === child.id ? '2px solid #2563EB' : 'none',
                outlineOffset: 2, borderRadius: 4, cursor: 'pointer', position: 'relative',
              }}
            >
              <CanvasRenderer comp={child} />
              {selectedChildId === child.id && (
                <div style={{ position: 'absolute', top: -16, left: 0, fontSize: 9, fontWeight: 600, background: '#2563EB', color: '#fff', padding: '1px 6px', borderRadius: '3px 3px 0 0', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                  #{i + 1} {child.type}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── DIVIDER ─────────────────────────────────────────────────────────────────
export function Divider({ comp }) {
  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', gap: comp.paddingH || 12,
    }}>
      <div style={{ flex: 1, height: comp.lineHeight || 1, background: comp.lineColor || '#E2E5EA' }} />
      {comp.label && (
        <>
          <span style={{ fontSize: comp.labelFontSize || 12, color: comp.labelColor || '#9CA3AF', background: comp.labelBg || '#F7F8FA', whiteSpace: 'nowrap', padding: '0 6px' }}>
            {comp.label}
          </span>
          <div style={{ flex: 1, height: comp.lineHeight || 1, background: comp.lineColor || '#E2E5EA' }} />
        </>
      )}
    </div>
  );
}
